/* istanbul ignore file */
// libs
import { ReactNode, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
// components
import { Show } from 'framework/show';
import { ShowSection, SectionRow, BigSectionRow, ShowPageLayout } from 'framework/ui/layout';
import {
  AddressField,
  EmailField,
  HtmlField,
  LinkField,
  PhoneField,
  TextField,
  DateField,
  FullDateField,
  TimezoneField,
} from 'framework/ui/field';
import IndustryField from 'domain/sales/shared/field/IndustryField';
import LeadMediumField from 'domain/sales/lead/shared/field/LeadMediumField';
import LeadStatusField from 'domain/sales/lead/shared/field/LeadStatusField';
import LeadSourceField from 'domain/sales/lead/shared/field/LeadSourceField';
import LeadGradeField from 'domain/sales/lead/shared/field/LeadGradeField';
import DisqualificationReasonField from 'domain/sales/lead/shared/field/DisqualificationReasonField';
import { AttachmentsListField } from 'domain/shared/artifact/field';
import DeleteLeadButton from 'domain/sales/lead/shared/button/DeleteLeadButton';
import ConvertButton from 'domain/sales/lead/shared/button/ConvertButton';
import MarkAsSpamButton from 'domain/sales/lead/shared/button/MarkAsSpamButton';
import EditLeadButton from 'domain/sales/lead/shared/button/EditLeadButton';
import ShowLeadPageMenu from 'domain/sales/lead/ShowLeadPageMenu';
import SingleLeadBreadcrumbs from 'domain/sales/lead/shared/SingleLeadBreadcrumbs';
import ConversionField from 'domain/sales/lead/shared/field/ConversionField';
// services
import useLeadService, { LEAD_STATUS, NOT_ACTIVE_STATUSES } from 'domain/sales/lead/shared/leadService';
import LeadEntityHeader from 'domain/sales/lead/LeadEntityHeader';
import AnalyticsClientIdField from 'domain/sales/lead/shared/field/AnalyticsClientIdField';
import { ReplaceUrlWithLink } from 'framework/utils';
import LeadAgeField from 'domain/sales/lead/shared/field/LeadAgeField';
import useLeadDuplicateService, { READ_LEADS_DUPLICATES } from 'domain/sales/lead/Show/leadDuplicateService';
import getDuplicatesWarning from 'domain/sales/lead/Show/leadDuplicateWarningBuilder';
import { Alert } from 'framework/ui/mui-builder';
import { useAuthorization } from 'framework/authorization';
import PostponedReasonField from 'domain/sales/lead/shared/field/PostponedReasonField';
import AssignLeadOwnerButton from 'domain/sales/lead/shared/button/AssignLeadOwnerButton';
import LeadWillAutoDisqualifiedSoonNotification from 'domain/sales/lead/shared/field/LeadWillAutoDisqualifiedSoonNotification';
import SendLeadTemplatedEnvelopeButton from 'domain/sales/lead/shared/button/SendLeadTemplatedEnvelopeButton';
import { SEND_TEMPLATED_ENVELOPE } from 'domain/sales/lead/docusign/templatedEnvelopeLeadService';
import DocuSignEnvelopeBox from 'domain/sales/shared/docusign/envelope/DocuSignEnvelopeBox';

const ViewLead = () => {
  const { id = '' } = useParams<Dictionary<string>>();
  const { getById, visit } = useLeadService();
  const { getDuplicates } = useLeadDuplicateService();
  const [duplicatesWarning, setDuplicatesWarning] = useState<ReactNode>(null);
  const { isGranted } = useAuthorization();

  useEffect(() => {
    if (isGranted(READ_LEADS_DUPLICATES)) {
      getDuplicates(id).then(duplicates => {
        setDuplicatesWarning(getDuplicatesWarning(duplicates)?.content);
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buttons = [
    <EditLeadButton key="editButton" isVisible={lead => LEAD_STATUS[lead.status] !== LEAD_STATUS.QUALIFIED} />,
    <SendLeadTemplatedEnvelopeButton key="sendEnvelope" permissionToCheck={SEND_TEMPLATED_ENVELOPE} />,
    <ConvertButton key="convertButton" />,
    <DeleteLeadButton key="deleteButton" />,
    <AssignLeadOwnerButton key="assignLeadOwnerButton" />,
    <MarkAsSpamButton key="markAsSpamButton" />,
  ];

  return (
    <Show
      getDataMethod={() => {
        visit(id);
        return getById(id);
      }}
    >
      <ShowPageLayout
        headerButtons={buttons}
        menu={<ShowLeadPageMenu />}
        breadcrumbs={<SingleLeadBreadcrumbs />}
        entityHeader={<LeadEntityHeader />}
        hasSeparateSections
      >
        <>
          <ShowSection title="NDA signing status" isVisible={isGranted('LEAD_READ_LEAD_FULL_INFO')}>
            <DocuSignEnvelopeBox source="envelopes" />
          </ShowSection>
          <ShowSection
            title="Lead information"
            justifyContent="space-between"
            actions={
              duplicatesWarning ? (
                <Alert severity="warning" sx={{ '&& a': { mr: 0 } }}>
                  {duplicatesWarning}
                </Alert>
              ) : undefined
            }
          >
            <LeadWillAutoDisqualifiedSoonNotification
              source="updatedAt"
              statusSource="status"
              skipStates={NOT_ACTIVE_STATUSES}
            />
            <SectionRow>
              <LeadAgeField source="leadAge.days" label="Company" />
              <EmailField source="email" />
              <EmailField source="additionalEmail" label="Additional email" />
              <PhoneField source="phoneNumber" label="Phone number" />
              <LinkField source="linkedInLink" label="LinkedIn" />
              <TextField source="id" label="Lead ID" />
            </SectionRow>
            <SectionRow>
              <TextField source="companyName" label="Company" />
              <LinkField source="websiteAddress" label="Website address" />
              <IndustryField source="industry" />
              <TextField source="employeesCount" label="No. of workers" />
              <LeadStatusField source="status" />
              <DisqualificationReasonField source="disqualificationReason" />
              <PostponedReasonField
                source="postponedReason"
                isVisible={data => data.getValueBySource('status') === 'POSTPONED'}
              />
              <DateField
                source="postponedReviewDate"
                label="Postponed review date"
                isVisible={(data: any) => data.getValueBySource('status') === 'POSTPONED'}
              />
              <LeadSourceField source="source" />
              <TextField source="sourceSite" label="Source site" />
              <LeadMediumField source="medium" />
              <TextField label="Marketing campaign" source="campaign" />
              <TextField label="Lead owner" source="ownerName" />
              <FullDateField label="First visit by owner at" source="ownerFirstVisitedAt" showSeconds={false} />
            </SectionRow>
          </ShowSection>
          <ShowSection title="Grade">
            <BigSectionRow>
              <LeadGradeField source="grade" isFull />
              <ReplaceUrlWithLink>
                <HtmlField source="gradingDetails" label="Grading details" />
              </ReplaceUrlWithLink>
            </BigSectionRow>
          </ShowSection>
          <ShowSection title="Address information">
            <AddressField source="address">
              <TimezoneField source="addressTimezone" />
            </AddressField>
          </ShowSection>
          <ShowSection title="Description">
            <BigSectionRow>
              <ReplaceUrlWithLink>
                <HtmlField source="description" label="" />
              </ReplaceUrlWithLink>
            </BigSectionRow>
          </ShowSection>
          <ShowSection title="Visitor details">
            <SectionRow>
              <TextField source="ip" label="IP address" />
              <TextField source="userAgent" label="User-Agent" />
              <LinkField source="httpRefer" label="HTTP referer" />
              <AnalyticsClientIdField source="googleAnalystClientId" label="Google Analytics client ID" />
              <TextField source="googleAdsClickId" label="Google Click ID" />
              <TextField source="requestId" label="Lead autocreation request ID" />
              <TextField source="utmDetails.utmId" label="UTM ID" />
              <TextField source="utmDetails.utmSource" label="UTM source" />
              <TextField source="utmDetails.utmMedium" label="UTM medium" />
              <TextField source="utmDetails.utmCampaign" label="UTM campagin" />
              <TextField source="utmDetails.utmTerm" label="UTM term" />
              <TextField source="utmDetails.utmContent" label="UTM content" />
              <ConversionField />
            </SectionRow>
          </ShowSection>
          <ShowSection title="Attachments">
            <AttachmentsListField source="attachmentsIds" label="" />
          </ShowSection>
        </>
      </ShowPageLayout>
    </Show>
  );
};

export default ViewLead;
